import React, { useEffect, useState } from 'react';
import SideDrawer from '../../../common/_custom/SideDrawer/SideDrawer';
import { fetchLenderCompanyList } from '../../../store/investor/service';
import { useAppSelector } from '../../../app/hooks';
import { StatusOption } from '../../Dashboard/DashboardModules/Overview/components/RelatedCompaniesNetwork/types';
import { getDealFormConfig } from '../../../store/investor/apiHelpers';
import { VIEW_CONFIGS } from '../../../utils/constants/commonConstants';
import OfficeIcon from '../../../assets/Svg/OfficeIcon';

interface LenderNetworkDrawerProps {
    open: boolean;
    onClose: () => void;
    lenderName: string;
    pnoId: string;
}

const SOURCE_LABELS: Record<string, string> = {
    DEBT_SCHEDULE: 'Debt Schedule',
    BUREAU: 'Credit Bureau',
    CHARGES: 'Charges',
};

const formatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const headerStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#000000',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
};

const cellStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#1C1B1F',
    display: 'flex',
    alignItems: 'center',
};

const gridColumns = '1.5fr 1fr 1fr 1fr 1fr 0.8fr';

const extractStatusOptions = (config: any, field: string): Record<string, StatusOption> => {
    const columns = config?.leadsColumns?.columns || [];
    const col = columns.find((c: any) => c?.field === field);
    return col?.dropdownOptions || {};
};

const LenderNetworkDrawer: React.FC<LenderNetworkDrawerProps> = ({ open, onClose, lenderName, pnoId }) => {
    const [companies, setCompanies] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [leadStatusOptions, setLeadStatusOptions] = useState<Record<string, StatusOption>>({});
    const [relationshipStatusOptions, setRelationshipStatusOptions] = useState<Record<string, StatusOption>>({});
    const userData = useAppSelector((state) => state.user.userData);

    useEffect(() => {
        if (!userData?.accountType || !userData?.investorOrgId || !userData?.primaryEmail) return;
        if (Object.keys(leadStatusOptions).length > 0) return;

        getDealFormConfig(
            {
                accountType: userData.accountType,
                investorOrgId: userData.investorOrgId,
                primaryEmail: userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (additionalDetailsConfig) => {
                    setLeadStatusOptions(extractStatusOptions(additionalDetailsConfig, 'companyStatus'));
                    setRelationshipStatusOptions(extractStatusOptions(additionalDetailsConfig, 'customFields.relationshipStatus'));
                },
            },
        );
    }, [userData?.accountType, userData?.investorOrgId, userData?.primaryEmail]);

    useEffect(() => {
        if (open && lenderName && pnoId) {
            loadCompanies();
        }
        if (!open) {
            setCompanies([]);
        }
    }, [open, lenderName, pnoId]);

    const loadCompanies = async () => {
        setIsLoading(true);
        try {
            const response = await fetchLenderCompanyList({ lenderName, pnoId });
            const responseData = response?.data?.responseData;
            if (responseData?.responseCode === 20) {
                setCompanies(response?.data?.data || []);
            }
        } catch (err) {
            console.error('Error fetching lender company list:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return (
        <SideDrawer heading="Funded Entities in Network" onClose={onClose} allowClose={true} showHeading={true} width="75%">
            <div style={{ padding: '16px 24px', overflowY: 'auto', height: 'calc(100% - 72px)' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading...</div>
                ) : companies.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No companies found</div>
                ) : (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: gridColumns,
                                padding: '12px 16px',
                                background: '#F9FAFB',
                                borderBottom: '1px solid #E5E7EB',
                            }}
                        >
                            <span style={headerStyle}>Company Name</span>
                            <span style={headerStyle}>Connection Status</span>
                            <span style={headerStyle}>Lead Status</span>
                            <span style={headerStyle}>Relationship Status</span>
                            <span style={headerStyle}>Data Source</span>
                            <span style={headerStyle}>Data as of</span>
                        </div>
                        {companies.map((company: any, index: number) => (
                            <div
                                key={company.orgId || index}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: gridColumns,
                                    padding: '10px 16px',
                                    borderBottom: index < companies.length - 1 ? '1px solid #F3F4F6' : 'none',
                                    alignItems: 'center',
                                }}
                            >
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            background: '#F2F2F2',
                                            padding: '0.25rem 0.625rem',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#1C1B1F',
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {company.orgName && (
                                            <OfficeIcon
                                                fill="#1C1B1F"
                                                width={16}
                                                height={13}
                                                style={{ flexShrink: 0, minWidth: 16, minHeight: 13 }}
                                            />
                                        )}
                                        {company.orgName || '-'}
                                    </span>
                                </div>
                                <div style={cellStyle}>
                                    <span
                                        style={{
                                            padding: '0.25rem 0.625rem',
                                            borderRadius: '12px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            ...(company.status === 'ACTIVE'
                                                ? { backgroundColor: '#E1FFE1', color: '#49A71B' }
                                                : { backgroundColor: '#EBEBEB', color: '#1C1B1F' }),
                                        }}
                                    >
                                        {company.status === 'ACTIVE' ? 'Active' : 'Closed'}
                                    </span>
                                </div>
                                <div style={cellStyle}>
                                    {company.leadStatus ? (
                                        (() => {
                                            const statusMeta = leadStatusOptions[company.leadStatus];
                                            return (
                                                <span
                                                    style={{
                                                        padding: '0.25rem 0.625rem',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        backgroundColor: statusMeta?.backgroundColor || '#F3F4F6',
                                                        color: statusMeta?.labelColor || '#1C1B1F',
                                                    }}
                                                >
                                                    {statusMeta?.label || company.leadStatus}
                                                </span>
                                            );
                                        })()
                                    ) : (
                                        <span style={{ color: '#9CA3AF', fontSize: '14px' }}>NA</span>
                                    )}
                                </div>
                                <div style={cellStyle}>
                                    {company.relationshipStatus ? (
                                        (() => {
                                            const statusMeta = relationshipStatusOptions[company.relationshipStatus];
                                            return (
                                                <span
                                                    style={{
                                                        padding: '0.25rem 0.625rem',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        backgroundColor: statusMeta?.backgroundColor || '#F3F4F6',
                                                        color: statusMeta?.labelColor || '#1C1B1F',
                                                    }}
                                                >
                                                    {statusMeta?.label || company.relationshipStatus}
                                                </span>
                                            );
                                        })()
                                    ) : (
                                        <span style={{ color: '#9CA3AF', fontSize: '14px' }}>NA</span>
                                    )}
                                </div>
                                <div style={{ ...cellStyle, gap: '4px', flexWrap: 'wrap' }}>
                                    {(company.sources || []).map((s: string, i: number) => (
                                        <span
                                            key={i}
                                            style={{
                                                padding: '0.25rem 0.625rem',
                                                borderRadius: '12px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                backgroundColor: '#EBEBEB',
                                                color: '#1C1B1F',
                                            }}
                                        >
                                            {SOURCE_LABELS[s] || s}
                                        </span>
                                    ))}
                                    {(!company.sources || company.sources.length === 0) && (
                                        <span style={{ color: '#9CA3AF', fontSize: '14px' }}>-</span>
                                    )}
                                </div>
                                <div style={{ ...cellStyle, color: '#9D9D9D', fontSize: '14px' }}>{formatDate(company.latestDataAsOf)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </SideDrawer>
    );
};

export default LenderNetworkDrawer;
